
import {StatusCode, StatusLikeCode, isStatusCode, resolveStatusCode, StatusItem} from "./status.types"

export class StatusCatalog {
    private readonly statuses: StatusItem[]
    private readonly byCode: Map<StatusCode, StatusItem>

    constructor(statuses: StatusItem[]) {
        this.statuses = [...statuses].sort((a, b) => a.order - b.order)
        this.byCode = new Map(this.statuses.map((status) => [status.code, status]))
    }

    getByCode(code: StatusLikeCode): StatusItem | undefined {
        const resolvedCode = resolveStatusCode(code)
        if (!isStatusCode(resolvedCode)) {
            return undefined
        }
        return this.byCode.get(resolvedCode)
    }

    indexOf(statusOrCode: StatusItem | StatusLikeCode): number {
        const status = typeof statusOrCode === "string" ? this.getByCode(statusOrCode) : statusOrCode
        if (!status) {
            return -1
        }

        return this.statuses.findIndex((item) => item.code === status.code)
    }

    isFirst(statusOrCode: StatusItem | StatusLikeCode): boolean {
        return this.indexOf(statusOrCode) === 0
    }

    isLast(statusOrCode: StatusItem | StatusLikeCode): boolean {
        return this.indexOf(statusOrCode) === this.statuses.length - 1
    }

    previousOf(statusOrCode: StatusItem | StatusLikeCode): StatusItem | null {
        const currentIndex = this.indexOf(statusOrCode)
        if (currentIndex <= 0) {
            return null
        }

        return this.statuses[currentIndex - 1] ?? null
    }

    nextOf(statusOrCode: StatusItem | StatusLikeCode): StatusItem | null {
        const currentIndex = this.indexOf(statusOrCode)
        if (currentIndex < 0 || currentIndex >= this.statuses.length - 1) {
            return null
        }

        return this.statuses[currentIndex + 1] ?? null
    }

    isHigherOrEqual(current: StatusItem | StatusLikeCode, required: StatusItem | StatusLikeCode): boolean {
        return this.indexOf(current) >= this.indexOf(required)
    }

    isBefore(current: StatusItem | StatusLikeCode, target: StatusItem | StatusLikeCode): boolean {
        // true, если current находится ниже target в линейке статусов.
        // Равные статусы не считаются "before".
        return this.indexOf(current) < this.indexOf(target)
    }

    isImmediatelyBefore(current: StatusItem | StatusLikeCode, target: StatusItem | StatusLikeCode): boolean {
        // true только для прямого предыдущего статуса без промежуточных рангов.
        return this.indexOf(current) + 1 === this.indexOf(target)
    }

    isAfter(current: StatusItem | StatusLikeCode, target: StatusItem | StatusLikeCode): boolean {
        return this.indexOf(current) > this.indexOf(target)
    }
}
